// Fase 6A + Fix Billing + Trial: Context central de suscripción.
// El plan funcional se determina desde workspaceMetadata server-side.
// El trial solo puede conceder PRO dentro del workspace personal.
// billingPlan local se conserva para UX, pero NO concede permisos.

import React, { createContext, useContext, useState, useEffect } from 'react';
import { PLANS, getPermissionsForPlan } from '@/config/subscriptionPlans';
import { db } from '@/firebase';
import { onSnapshot, doc, collection } from 'firebase/firestore';
import { useAuth } from '@/context/AuthContext';
import {
  initBillingClient,
  querySubscriptionProducts,
  addPurchaseListener,
  mapPurchaseToPlan,
  verifyPurchaseWithBackend,
} from '@/services/billingService';
import { getTrialStatus } from '@/services/trialService';

const SubscriptionContext = createContext(null);

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);

  if (!ctx) {
    throw new Error('useSubscription debe usarse dentro de <SubscriptionProvider>');
  }

  return ctx;
}

export function SubscriptionProvider({ children, workspaceId }) {
  const { user } = useAuth();
  const uid = user?.uid ?? null;

  // Plan funcional actual.
  const [plan, _setPlan] = useState(PLANS.FREE);

  const [loadingSubscription] = useState(false);
  const [cachedProducts, setCachedProducts] = useState([]);

  // Plan detectado localmente por Google Play.
  // Se conserva para UX, pero NO concede permisos.
  const [billingPlan, setBillingPlan] = useState(null);

  // Plan efectivo server-side del workspace actualmente abierto.
  const [workspaceMetadataPlan, setWorkspaceMetadataPlan] = useState(null);

  // Entitlement interno del usuario.
  const [entitlementPlan, setEntitlementPlan] = useState(null);

  const [isPlanReady, setIsPlanReady] = useState(false);

  // null = no iniciado
  // true = Billing disponible
  // false = Billing no disponible
  const [billingAvailable, setBillingAvailable] = useState(null);

  // Trial Pro 60 días.
  const [trialPlan, setTrialPlan] = useState(null);
  const [trialExpiresAt, setTrialExpiresAt] = useState(null);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);

  // Testing manual desde DevTools.
  const setPlan = (newPlan) => {
    _setPlan(newPlan);
  };

  // ── Calcular plan efectivo ──
  //
  // Autoridad funcional:
  //
  // 1. workspaceMetadata del workspace actual.
  // 2. Trial, únicamente si el usuario está en su workspace personal.
  // 3. FREE.
  //
  // billingPlan local NO concede permisos.
  useEffect(() => {
    let effectivePlan;

    const isOwnWorkspace = Boolean(
      uid &&
      workspaceId &&
      uid === workspaceId
    );

    if (workspaceMetadataPlan === PLANS.PREMIUM_PRO) {
      effectivePlan = PLANS.PREMIUM_PRO;
    } else if (workspaceMetadataPlan === PLANS.PREMIUM_PERSONAL) {
      effectivePlan = PLANS.PREMIUM_PERSONAL;
    } else if (
      isOwnWorkspace &&
      trialPlan === PLANS.PREMIUM_PRO
    ) {
      effectivePlan = PLANS.PREMIUM_PRO;
    } else {
      effectivePlan = PLANS.FREE;
    }

    _setPlan(effectivePlan);
  }, [
    workspaceMetadataPlan,
    trialPlan,
    uid,
    workspaceId,
  ]);

  // ── Inicialización de Billing ──
  //
  // Se ejecuta una sola vez durante la vida de la app.
  // Billing es global al dispositivo y NO está scoped al usuario Firebase.
  useEffect(() => {
    async function initBilling() {
      const result = await initBillingClient();

      if (!result.connected) {
        console.warn(
          '[BILLING] BillingClient no conectado — Billing no disponible'
        );
        setBillingAvailable(false);
        return;
      }

      setBillingAvailable(true);

      // Cachear productos para PlanUpgradeModal.
      const products = await querySubscriptionProducts();
      setCachedProducts(products);

      // No consultar compras activas automáticamente al arrancar.
      //
      // Google Play Billing devuelve compras de la cuenta Google
      // del dispositivo y no del usuario Firebase.
      //
      // La autoridad funcional está ahora en Firestore:
      // workspaceMetadata/{workspaceId}.effectivePlan.

      // Listener para compras completadas en tiempo real.
      addPurchaseListener(async (data) => {
        if (
          data.responseCode === 0 &&
          data.purchases?.length > 0
        ) {
          for (const purchase of data.purchases) {
            const newPlan = mapPurchaseToPlan(purchase);

            if (newPlan && purchase.purchaseToken) {
              try {
                // Verificar primero la compra en backend.
                await verifyPurchaseWithBackend(
                  purchase.purchaseToken
                );

                console.log(
                  '[BILLING] Purchase verified with backend, awaiting server entitlement...'
                );

                // billingPlan se mantiene únicamente para UX.
                // NO participa en permissions ni en el plan funcional.
                setBillingPlan(newPlan);
              } catch (error) {
                console.warn(
                  '[BILLING] Purchase verification failed, not accepting locally:',
                  error
                );
              }

              break;
            }
          }
        }
      });
    }

    initBilling();
  }, []);

  // ── Internal entitlement + Trial ──
  //
  // Dependientes del UID Firebase.
  useEffect(() => {
    let cancelled = false;

    // Limpiar estado del usuario anterior.
    setEntitlementPlan(null);
    setTrialPlan(null);
    setTrialExpiresAt(null);
    setTrialDaysRemaining(0);
    setIsPlanReady(false);

    if (!uid) {
      setIsPlanReady(true);
      return;
    }

    let entitlementResolved = false;
    let trialResolved = false;

    const maybeReady = () => {
      if (
        entitlementResolved &&
        trialResolved &&
        !cancelled
      ) {
        setIsPlanReady(true);
      }
    };

    // Trial.
    getTrialStatus(uid)
      .then((trialStatus) => {
        if (cancelled) return;

        if (trialStatus.hasActiveTrial) {
          setTrialPlan(PLANS.PREMIUM_PRO);
          setTrialExpiresAt(
            trialStatus.trialExpiresAt
          );
          setTrialDaysRemaining(
            trialStatus.daysRemaining
          );
        }

        trialResolved = true;
        maybeReady();
      })
      .catch((trialErr) => {
        if (cancelled) return;

        console.warn(
          '[TRIAL] Error checking trial status:',
          trialErr?.message
        );

        trialResolved = true;
        maybeReady();
      });

    // Internal entitlement.
    const unsubscribe = onSnapshot(
      doc(
        collection(db, 'internalEntitlements'),
        uid
      ),
      (docSnap) => {
        if (cancelled) return;

        if (docSnap.exists()) {
          const data = docSnap.data();

          setEntitlementPlan(
            data.active === true &&
            data.plan === PLANS.PREMIUM_PRO
              ? PLANS.PREMIUM_PRO
              : null
          );
        } else {
          setEntitlementPlan(null);
        }

        entitlementResolved = true;
        maybeReady();
      },
      (error) => {
        if (cancelled) return;

        console.error(
          '[ENTITLEMENT] Error loading internal entitlement:',
          error
        );
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [uid]);

  // ── WorkspaceMetadata ──
  //
  // Esta es la fuente server-authoritative del plan funcional
  // del workspace actualmente abierto.
  //
  // Si workspaceId === uid:
  //   estamos en el workspace personal.
  //
  // Si workspaceId !== uid:
  //   estamos en un workspace externo y solo aplica el plan
  //   de ese workspace.
  useEffect(() => {
    let cancelled = false;

    setWorkspaceMetadataPlan(null);

    if (!workspaceId) {
      return;
    }

    const unsubscribe = onSnapshot(
      doc(
        collection(db, 'workspaceMetadata'),
        workspaceId
      ),
      (docSnap) => {
        if (cancelled) return;

        if (docSnap.exists()) {
          const data = docSnap.data();
          const effectivePlan = data.effectivePlan;

          if (effectivePlan === 'PREMIUM_PRO') {
            setWorkspaceMetadataPlan(
              PLANS.PREMIUM_PRO
            );
          } else if (
            effectivePlan === 'PREMIUM_PERSONAL'
          ) {
            setWorkspaceMetadataPlan(
              PLANS.PREMIUM_PERSONAL
            );
          } else {
            setWorkspaceMetadataPlan(null);
          }
        } else {
          setWorkspaceMetadataPlan(null);
        }
      },
      (error) => {
        if (cancelled) return;

        console.error(
          '[WORKSPACE_METADATA] Error loading workspace metadata:',
          error
        );

        setWorkspaceMetadataPlan(null);
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [workspaceId]);

  // ── Restaurar compras ──
  //
  // Acción manual de recuperación.
  // billingService verifica primero la compra contra backend.
  const restorePurchases = async () => {
    const {
      restorePurchases: billingRestore,
    } = await import(
      '@/services/billingService'
    );

    const restoredPlan = await billingRestore();

    // Solo se conserva para UX local.
    // No concede permisos.
    setBillingPlan(restoredPlan);

    return restoredPlan;
  };

  // Los permisos dependen exclusivamente del plan funcional
  // calculado arriba.
  const permissions = getPermissionsForPlan(plan);

  const value = {
    plan,

    isFree:
      plan === PLANS.FREE,

    isPremiumPersonal:
      plan === PLANS.PREMIUM_PERSONAL,

    isPremiumPro:
      plan === PLANS.PREMIUM_PRO,

    billingPlan,
    loadingSubscription,

    // Solo para testing interno.
    setPlan,

    permissions,

    cachedProducts,

    restorePurchases,

    isPlanReady,

    billingAvailable,

    trialPlan,
    trialExpiresAt,
    trialDaysRemaining,

    // Se mantiene por compatibilidad con consumidores actuales.
    entitlementPlan,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}
